import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, LayoutList, Plus, Play, Check, AlertTriangle, Trash2, X,
  Clock, User, Pause, Inbox, Bell,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const KIND = { in_house: 'bg-emerald-100 text-emerald-700', outsourced: 'bg-amber-100 text-amber-700' };
const STATUS = {
  pending: { label: 'Pending', cls: 'bg-gray-100 text-gray-600' },
  in_progress: { label: 'In progress', cls: 'bg-blue-100 text-blue-700' },
  on_hold: { label: 'On hold', cls: 'bg-orange-100 text-orange-700' },
  awaiting_qc: { label: 'Awaiting QC', cls: 'bg-amber-100 text-amber-700' },
  sent: { label: 'Sent out', cls: 'bg-indigo-100 text-indigo-700' },
  done: { label: 'Done', cls: 'bg-green-100 text-green-700' },
};

const dueLabel = (j) => {
  if (j.days_left == null) return null;
  if (j.overdue) return `Overdue by ${Math.abs(j.days_left)}d`;
  return `Due in ${j.days_left}d`;
};

const EMPTY = { order_ref: '', order_id: '', order_type: 'customer', checkpoint_key: '', owner_party_id: '', promised_days: '', notes: '' };

export default function OrderJobs() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isManager = ['admin', 'production_supervisor'].includes(currentUser?.role);
  const isQC = ['admin', 'production_supervisor', 'qc'].includes(currentUser?.role);

  const [tab, setTab] = useState(isManager ? 'board' : 'mine');
  const [board, setBoard] = useState({ active: [], counts: {} });
  const [mine, setMine] = useState([]);
  const [routes, setRoutes] = useState({});
  const [orderTypes, setOrderTypes] = useState([]);
  const [parties, setParties] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [issueOpen, setIssueOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState(null);
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const calls = [api.get('/order-jobs/my')];
      if (isManager) calls.push(api.get('/order-jobs/board'), api.get('/order-config/route-templates'), api.get('/order-config/order-types'), api.get('/order-config/parties'), api.get('/order-jobs/orders-lookup'));
      const res = await Promise.all(calls);
      setMine(res[0].data);
      if (isManager) {
        setBoard(res[1].data);
        setRoutes(res[2].data);
        setOrderTypes(res[3].data);
        setParties(res[4].data.filter(p => p.is_active !== false));
        setOrders(res[5].data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load jobs');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const checkpoints = routes[form.order_type] || [];
  const openIssue = () => { setForm(EMPTY); setIssueOpen(true); };

  const submitIssue = async () => {
    const cp = checkpoints.find(c => c.key === form.checkpoint_key);
    if (!form.order_ref.trim()) { toast.error('Order reference required'); return; }
    if (!cp) { toast.error('Pick a checkpoint'); return; }
    if (!form.owner_party_id) { toast.error('Pick an owner'); return; }
    if (!form.promised_days || Number(form.promised_days) <= 0) { toast.error('Enter promised days'); return; }
    setSaving(true);
    try {
      await api.post('/order-jobs/issue', {
        order_ref: form.order_ref.trim(), order_id: form.order_id || null, order_type: form.order_type,
        checkpoint_key: cp.key, checkpoint_name: cp.name, mode: cp.mode, qc: cp.qc, gate: cp.gate,
        owner_party_id: form.owner_party_id, promised_days: Number(form.promised_days),
        notes: form.notes,
      });
      toast.success('Job issued & owner notified');
      setIssueOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to issue'); }
    finally { setSaving(false); }
  };

  const setStatus = async (j, status) => {
    try { await api.patch(`/order-jobs/${j.id}`, { status }); toast.success(`Marked ${STATUS[status]?.label || status}`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const escalate = async (j) => {
    try { const r = await api.post(`/order-jobs/${j.id}/escalate`); toast.success(`Escalated · ${r.data.notified} notified`); }
    catch (e) { toast.error('Failed'); }
  };
  const qcPass = async (j) => {
    try {
      await api.post(`/order-jobs/${j.id}/qc`, { result: 'pass' });
      toast.success('QC passed');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const openAction = (type, job) => setAction({ type, job, reason: '', center: '', docket: '', cert: '' });
  const submitAction = async () => {
    const { type, job } = action;
    const reason = (action.reason || '').trim();
    const cert = (action.cert || '').trim();
    if (type === 'qc-fail' && !reason) { toast.error('A reason is required to fail QC'); return; }
    if (type === 'receive' && !cert) { toast.error('Certificate / HUID number is required'); return; }
    setActing(true);
    try {
      if (type === 'qc-fail') {
        await api.post(`/order-jobs/${job.id}/qc`, { result: 'fail', reason });
        toast.success('Sent back for rework');
      } else if (type === 'send') {
        await api.post(`/order-jobs/${job.id}/dispatch`, { action: 'send', center: (action.center || '').trim() || null, docket: (action.docket || '').trim() || null });
        toast.success('Marked sent');
      } else if (type === 'receive') {
        await api.post(`/order-jobs/${job.id}/dispatch`, { action: 'receive', certificate_no: cert });
        toast.success('Received & closed');
      } else if (type === 'delete') {
        await api.delete(`/order-jobs/${job.id}`);
        toast.success('Deleted');
      }
      setAction(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActing(false); }
  };

  const grouped = useMemo(() => {
    const g = {};
    (board.active || []).forEach(j => { (g[j.order_ref] = g[j.order_ref] || []).push(j); });
    return g;
  }, [board]);

  const JobCard = ({ j, showOrder }) => (
    <div data-testid={`job-${j.id}`} className={`rounded-lg border p-2.5 bg-white ${j.overdue ? 'border-red-300' : 'border-gray-100'}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-sm text-gray-800">{j.checkpoint_name}</span>
            {j.mode === 'parallel' && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">Parallel</span>}
            {j.gate && <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-600">Gate</span>}
            {j.qc && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">QC</span>}
            {j.roundtrip && <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600">Send/Receive</span>}
            <span className={`text-[9px] px-1.5 py-0.5 rounded ${STATUS[j.status]?.cls}`}>{STATUS[j.status]?.label}</span>
          </div>
          {showOrder && <p className="text-[11px] text-gray-400 mt-0.5">Order {j.order_ref} · {j.order_type}</p>}
          {j.status === 'awaiting_qc' && <p className="text-[10px] text-amber-600 mt-0.5">Waiting for QC review</p>}
          {j.status === 'sent' && j.dispatch?.center && <p className="text-[10px] text-indigo-600 mt-0.5">At {j.dispatch.center}{j.dispatch.docket ? ` · ${j.dispatch.docket}` : ''}</p>}
          {j.rework_count > 0 && <p className="text-[10px] text-red-500 mt-0.5">Rework ×{j.rework_count}{j.last_qc_fail?.reason ? ` · ${j.last_qc_fail.reason}` : ''}{j.last_qc_fail?.by ? ` — ${j.last_qc_fail.by}` : ''}{j.last_qc_fail?.at ? ` · ${new Date(j.last_qc_fail.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''}</p>}
          {j.dispatch?.certificate_no && <p className="text-[10px] text-green-600 mt-0.5">Cert #{j.dispatch.certificate_no}</p>}
          <div className="flex items-center gap-2 mt-1 flex-wrap text-[11px]">
            <span className="flex items-center gap-1 text-gray-600"><User className="w-3 h-3" />{j.owner_name}</span>
            <span className={`px-1.5 py-0.5 rounded ${KIND[j.owner_kind] || 'bg-gray-100 text-gray-500'}`}>{j.owner_kind === 'in_house' ? 'In-house' : 'Outsourced'}</span>
            {dueLabel(j) && <span className={`flex items-center gap-1 font-semibold ${j.overdue ? 'text-red-600' : 'text-gray-500'}`}><Clock className="w-3 h-3" />{dueLabel(j)}</span>}
          </div>
        </div>
      </div>
      <div className="flex gap-1.5 mt-2 flex-wrap">
        {j.roundtrip ? (
          <>
            {j.status !== 'done' && j.status !== 'sent' && isQC && <button onClick={() => openAction('send', j)} data-testid={`send-${j.id}`} className="text-[11px] px-2.5 py-1 rounded-full bg-indigo-600 text-white flex items-center gap-1"><Play className="w-3 h-3" /> Mark Sent</button>}
            {j.status === 'sent' && isQC && <button onClick={() => openAction('receive', j)} data-testid={`receive-${j.id}`} className="text-[11px] px-2.5 py-1 rounded-full bg-green-600 text-white flex items-center gap-1"><Check className="w-3 h-3" /> Mark Received</button>}
          </>
        ) : j.status === 'awaiting_qc' ? (
          isQC && (
            <>
              <button onClick={() => qcPass(j)} data-testid={`qc-pass-${j.id}`} className="text-[11px] px-2.5 py-1 rounded-full bg-green-600 text-white flex items-center gap-1"><Check className="w-3 h-3" /> QC Pass</button>
              <button onClick={() => openAction('qc-fail', j)} data-testid={`qc-fail-${j.id}`} className="text-[11px] px-2.5 py-1 rounded-full bg-red-600 text-white flex items-center gap-1"><X className="w-3 h-3" /> QC Fail</button>
            </>
          )
        ) : (
          <>
            {j.status === 'pending' && <button onClick={() => setStatus(j, 'in_progress')} data-testid={`start-${j.id}`} className="text-[11px] px-2.5 py-1 rounded-full bg-blue-600 text-white flex items-center gap-1"><Play className="w-3 h-3" /> Start</button>}
            {j.status !== 'done' && <button onClick={() => setStatus(j, 'done')} data-testid={`done-${j.id}`} className="text-[11px] px-2.5 py-1 rounded-full bg-green-600 text-white flex items-center gap-1"><Check className="w-3 h-3" /> {j.qc ? 'Submit for QC' : 'Done'}</button>}
            {j.status === 'in_progress' && <button onClick={() => setStatus(j, 'on_hold')} className="text-[11px] px-2.5 py-1 rounded-full bg-orange-500 text-white flex items-center gap-1"><Pause className="w-3 h-3" /> Hold</button>}
            {j.status === 'on_hold' && <button onClick={() => setStatus(j, 'in_progress')} className="text-[11px] px-2.5 py-1 rounded-full bg-blue-600 text-white flex items-center gap-1"><Play className="w-3 h-3" /> Resume</button>}
          </>
        )}
        {isManager && j.overdue && <button onClick={() => escalate(j)} data-testid={`escalate-${j.id}`} className="text-[11px] px-2.5 py-1 rounded-full bg-red-600 text-white flex items-center gap-1"><Bell className="w-3 h-3" /> Escalate</button>}
        {isManager && <button onClick={() => openAction('delete', j)} data-testid={`delete-job-${j.id}`} className="text-[11px] px-2.5 py-1 rounded-full border border-gray-200 text-gray-400 flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-[#075E54] to-[#128C7E] text-white px-4 py-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-full hover:bg-white/20"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold leading-tight">Work Board</h1>
            <p className="text-xs text-emerald-200">Who holds what now · promised-time tracking</p>
          </div>
          {isManager && <button onClick={openIssue} data-testid="issue-job-btn" className="bg-white text-[#075E54] font-semibold text-sm px-3 py-1.5 rounded-full flex items-center gap-1"><Plus className="w-4 h-4" /> Issue</button>}
        </div>
        {isManager && (
          <div className="flex gap-3 mt-3 text-center">
            <div className="flex-1 bg-emerald-800/40 rounded-lg py-1.5"><p className="text-lg font-bold leading-none">{board.counts?.active || 0}</p><p className="text-[10px] text-emerald-200">Active</p></div>
            <div className="flex-1 bg-emerald-800/40 rounded-lg py-1.5"><p className="text-lg font-bold leading-none">{board.counts?.in_progress || 0}</p><p className="text-[10px] text-emerald-200">In progress</p></div>
            <div className="flex-1 bg-red-500/30 rounded-lg py-1.5"><p className="text-lg font-bold leading-none">{board.counts?.overdue || 0}</p><p className="text-[10px] text-red-100">Overdue</p></div>
          </div>
        )}
        <div className="flex gap-1.5 mt-3 bg-emerald-800/40 rounded-full p-1">
          {isManager && <button onClick={() => setTab('board')} data-testid="tab-board" className={`flex-1 py-1.5 rounded-full text-xs font-semibold flex items-center justify-center gap-1 ${tab === 'board' ? 'bg-white text-[#075E54]' : 'text-emerald-100'}`}><LayoutList className="w-3.5 h-3.5" /> Board</button>}
          <button onClick={() => setTab('mine')} data-testid="tab-mine" className={`flex-1 py-1.5 rounded-full text-xs font-semibold flex items-center justify-center gap-1 ${tab === 'mine' ? 'bg-white text-[#075E54]' : 'text-emerald-100'}`}><Inbox className="w-3.5 h-3.5" /> My Work</button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" /></div>
      ) : tab === 'board' ? (
        <div className="flex-1 overflow-y-auto px-4 pt-3 pb-24 space-y-4">
          {Object.keys(grouped).length === 0 ? (
            <div className="text-center py-12 text-gray-400"><LayoutList className="w-12 h-12 mx-auto mb-2 opacity-30" /><p className="text-sm">No active jobs. Issue one to begin.</p></div>
          ) : Object.entries(grouped).map(([ref, jobs]) => (
            <div key={ref}>
              <p className="text-xs font-bold text-gray-500 uppercase mb-1.5">Order {ref} <span className="text-gray-300">· {jobs.length} job(s)</span></p>
              <div className="space-y-1.5">{jobs.map(j => <JobCard key={j.id} j={j} showOrder={false} />)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 pt-3 pb-24 space-y-1.5">
          {mine.length === 0 ? (
            <div className="text-center py-12 text-gray-400"><Inbox className="w-12 h-12 mx-auto mb-2 opacity-30" /><p className="text-sm">No jobs assigned to you.</p></div>
          ) : mine.map(j => <JobCard key={j.id} j={j} showOrder={true} />)}
        </div>
      )}

      {issueOpen && (
        <div className="fixed inset-0 z-50 flex items-end" onClick={() => !saving && setIssueOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative w-full bg-white rounded-t-3xl px-5 pt-5 pb-10 space-y-3 max-w-lg mx-auto max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="issue-modal">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-1" />
            <div className="flex items-center justify-between"><h2 className="font-bold text-gray-900 text-base">Issue Job</h2><button onClick={() => setIssueOpen(false)}><X className="w-5 h-5 text-gray-400" /></button></div>
            <select value={form.order_id} onChange={e => { const o = orders.find(x => x.id === e.target.value); setForm(f => ({ ...f, order_id: e.target.value, order_ref: o ? o.order_number : '', order_type: o ? o.order_type : f.order_type, checkpoint_key: '' })); }} data-testid="job-order-select" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-white">
              <option value="">Select order…</option>
              {orders.map(o => <option key={o.id} value={o.id}>{o.order_number} · {o.main_group || '—'} · {o.customer_name || '—'}</option>)}
            </select>
            {orders.length === 0 && <p className="text-[11px] text-amber-600">No active orders found — create an order first.</p>}
            {form.order_id && <p className="text-[11px] text-gray-500">Order type: <b>{orderTypes.find(t => t.key === form.order_type)?.label || form.order_type}</b></p>}
            <select value={form.checkpoint_key} onChange={e => setForm(f => ({ ...f, checkpoint_key: e.target.value }))} data-testid="job-checkpoint" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-white">
              <option value="">Select checkpoint…</option>
              {checkpoints.map(c => <option key={c.key} value={c.key}>{c.name}{c.gate ? ' (gate)' : ''}{c.mode === 'parallel' ? ' · parallel' : ''}</option>)}
            </select>
            <select value={form.owner_party_id} onChange={e => setForm(f => ({ ...f, owner_party_id: e.target.value }))} data-testid="job-owner" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-white">
              <option value="">Select owner (karigar / vendor)…</option>
              {parties.map(p => <option key={p.id} value={p.id}>{p.name} · {p.party_kind === 'in_house' ? 'In-house' : 'Outsourced'}</option>)}
            </select>
            {parties.length === 0 && <p className="text-[11px] text-amber-600">No parties yet — add karigars/vendors in Order Setup first.</p>}
            <input type="number" min="0.5" step="0.5" placeholder="Promised days (as committed by owner)" value={form.promised_days} onChange={e => setForm(f => ({ ...f, promised_days: e.target.value }))} data-testid="job-days" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm" />
            <input placeholder="Notes (optional)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm" />
            <button onClick={submitIssue} disabled={saving} data-testid="submit-issue-btn" className="w-full bg-emerald-600 text-white font-bold py-3.5 rounded-xl disabled:opacity-60">{saving ? 'Issuing…' : 'Issue & Notify Owner'}</button>
          </div>
        </div>
      )}

      {action && (
        <div className="fixed inset-0 z-50 flex items-end" onClick={() => !acting && setAction(null)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative w-full bg-white rounded-t-3xl px-5 pt-5 pb-10 space-y-3 max-w-lg mx-auto max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="job-action-modal">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-1" />
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-gray-900 text-base">
                {action.type === 'qc-fail' && 'Fail QC — Send for rework'}
                {action.type === 'send' && 'Mark Sent Out'}
                {action.type === 'receive' && 'Mark Received'}
                {action.type === 'delete' && 'Delete Job'}
              </h2>
              <button onClick={() => setAction(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <p className="text-[12px] text-gray-500">{action.job.checkpoint_name} · Order {action.job.order_ref}</p>

            {action.type === 'qc-fail' && (
              <>
                <label className="block text-xs font-semibold text-gray-600">Reason for QC fail <span className="text-red-500">*</span></label>
                <textarea autoFocus rows={3} value={action.reason} onChange={e => setAction(a => ({ ...a, reason: e.target.value }))} placeholder="What needs to be reworked?" data-testid="qc-fail-reason" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none" />
              </>
            )}

            {action.type === 'send' && (
              <>
                <label className="block text-xs font-semibold text-gray-600">Hallmark centre / lab <span className="text-gray-400">(optional)</span></label>
                <input value={action.center} onChange={e => setAction(a => ({ ...a, center: e.target.value }))} placeholder="e.g. BIS Centre, Zaveri Bazaar" data-testid="send-center" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm" />
                <label className="block text-xs font-semibold text-gray-600">Docket / reference no. <span className="text-gray-400">(optional)</span></label>
                <input value={action.docket} onChange={e => setAction(a => ({ ...a, docket: e.target.value }))} placeholder="Tracking / docket number" data-testid="send-docket" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm" />
              </>
            )}

            {action.type === 'receive' && (
              <>
                <label className="block text-xs font-semibold text-gray-600">Certificate / HUID number <span className="text-red-500">*</span></label>
                <input autoFocus value={action.cert} onChange={e => setAction(a => ({ ...a, cert: e.target.value }))} placeholder="Enter certificate / HUID to close" data-testid="receive-cert" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm" />
              </>
            )}

            {action.type === 'delete' && (
              <p className="text-sm text-gray-600">Are you sure you want to delete this job? This cannot be undone.</p>
            )}

            <button onClick={submitAction} disabled={acting} data-testid="job-action-submit" className={`w-full text-white font-bold py-3.5 rounded-xl disabled:opacity-60 ${action.type === 'delete' || action.type === 'qc-fail' ? 'bg-red-600' : 'bg-emerald-600'}`}>
              {acting ? 'Working…' : (
                action.type === 'qc-fail' ? 'Send for Rework' :
                action.type === 'send' ? 'Confirm Sent' :
                action.type === 'receive' ? 'Confirm Received' : 'Delete Job'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
